import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { Helmet } from 'react-helmet-async';
import { Meta } from '../../interfaces/Meta/Meta';
import { settingsAtom } from '../../atoms/Settings';
import { BreadcrumbElement } from '../../interfaces/Breadcrumb/BreadcrumbElement';

type GenericHelmetProps = {
  title: string;
  meta?: Meta;
  breadcrumb?: BreadcrumbElement[];
};

export const GenericHelmet = ({
  title,
  meta,
  breadcrumb = [],
}: GenericHelmetProps) => {
  const FE_URL = process.env.REACT_APP_FE_URL;
  const settings = useRecoilValue(settingsAtom);

  const defaultTitle = `${meta?.title || title} | ${
    settings.settingsV1.storefront_navbar_text || 'NNET E-Ticaret'
  }`;

  const hiddenTitle = settings.settingsV1.storefront_hidden_title;

  const titleRef = useRef(defaultTitle);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hiddenTitle) {
        document.title = hiddenTitle;
      } else {
        document.title = titleRef.current;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <Helmet>
      <title>{defaultTitle}</title>

      {!!meta?.title && <meta name='title' content={meta.title} />}
      {!!meta?.description && (
        <meta name='description' content={meta.description} />
      )}

      {meta?.canonicalURL !== undefined && (
        <link
          rel='canonical'
          href={`${FE_URL}${meta.canonicalURL ? `/${meta.canonicalURL}` : ''}`}
        />
      )}

      {!!breadcrumb?.length && (
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: settings.settingsV1.storefront_navbar_text || 'Ana Sayfa',
                item: `${FE_URL}/`,
              },
              ...breadcrumb.map((el, i) => ({
                '@type': 'ListItem',
                position: i + 2,
                name: el.name,
                item: `${FE_URL}/${el.url}`,
              })),
            ],
          })}
        </script>
      )}
    </Helmet>
  );
};
